import {
  CoverPage,
  uploadCoverPageImage,
  useCreateCoverPage,
  useDeleteCoverPage,
  useGetCoverPages,
  useUpdateCoverPage,
} from "@/api/cover-page";
import {
  useGenerateListTextPrint,
  useGenerateMultipleprintsPdf,
} from "@/api/pdf";
import {
  PrintModel,
  PrintTab,
  getTabsForModel,
} from "@/config/print-model-tabs.config";
import { useGetAllAttachments } from "@/api/attachment";
import { useGetAllMultiplePrints } from "@/api/multipleprint/multipleprint";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import i18n, { SupportedLocale } from "@/i18n";
import { getDefaultImage } from "@/lib/image";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { FieldPresenceProvider } from "@/lib/print-field-presence/FieldPresenceContext";
import { useFieldPresenceContext } from "@/lib/print-field-presence/field-presence.context";
import { usePresentPrintFields } from "@/lib/print-field-presence/usePresentPrintFields";
import {
  PresenceLoadingHint,
  presenceLoadingClassName,
} from "@/lib/print-field-presence/PresenceLoadingHint";
import { ShowAllFieldsToggle } from "@/lib/print-field-presence/ShowAllFieldsToggle";
import { isEmptyRichText, normalizeRichText } from "@/lib/utils";
import {
  AlertCircle,
  BookMarked,
  Check,
  ChevronDown,
  CloudUpload,
  Loader2,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PDFViewer from "../print/PDFViewer";
import PrintDrawer from "../print/PrintDrawer";
import SelectedRecord from "../print/SelectedRecord";
import Tiptap from "../tiptap/TipTap";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FileDropzone, FileItem } from "../ui/FileDropzone";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Input } from "../ui/input";
import { LoadImageDropzoneCta } from "../form-fields/LoadImageDropzoneCta";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabsContained";
import { Thumbnail } from "../ui/tumbnail";
import {
  EXEMPLAIRE_FIELD_LABELS,
  EXEMPLAIRE_ONLY_SETTING_KEYS,
  exemplaireFieldKeys,
  getAllSettingsFields,
  getGroupFields,
  getSettingsGroups,
  MULTIPLEPRINT_SETTING_LABELS,
  MultiplePrintSettings,
  MultiplePrintTemplate,
} from "./multipleprintPrintSettings";

type MultiplePrintDrawerProps = PropsWithChildren<{
  multipleprintIds: string[];
  menuItemTrigger?: boolean;
  labeledTrigger?: boolean;
}>;

/** Le modèle « vignettes » (liste-texte) s'ajoute aux 3 fiches. */
type DrawerTemplate = MultiplePrintTemplate | "vignettes";

/** Champs du résumé court du modèle Vignettes. */
type VignetteSummaryFields = {
  registrationNumber: boolean;
  artist: boolean;
  title: boolean;
  creationDate: boolean;
  dimensions: boolean;
  techniqueType: boolean;
};

type FormValues = {
  template: DrawerTemplate;
  settings: MultiplePrintSettings;
  itemsPerPage?: "1" | "3";
  coverPageTitle?: string;
  coverPageParagraph1?: string;
  coverPageParagraph2?: string;
  coverImagePosition?: "header" | "after_title" | "after_para1" | "footer";
  coverImageSize?: "large" | "small";
  coverImageFile?: File | null;
  showPagination?: boolean;
  showPrintDate?: boolean;
  binderImageSize?: "large" | "small";
  textAlignment?: "center" | "left" | "right";
  headerAlignment?: "center" | "left" | "right";
  imageAlignment?: "center" | "left" | "right";
  listTitle?: string;
  // Images secondaires (appliquées à chaque multiple)
  selectedAttachmentIds: string[];
  // Modèle Vignettes (liste-texte)
  showThumbnails?: boolean;
  summaryFields?: VignetteSummaryFields;
};

/** Réglages par défaut d'un modèle : tout désélectionné (retour client). */
function defaultSettingsFor(
  _template: MultiplePrintTemplate,
): MultiplePrintSettings {
  const settings: MultiplePrintSettings = getAllSettingsFields();
  settings.exemplaireFields = {};
  return settings;
}

const templateToModel = (template: DrawerTemplate): PrintModel => {
  if (template === "simple") return PrintModel.SIMPLE;
  if (template === "binder") return PrintModel.CLASSEUR;
  if (template === "vignettes") return PrintModel.LIST_TEXT;
  return PrintModel.COMPLET;
};

const printTabToValue = (tab: PrintTab): string => {
  switch (tab) {
    case PrintTab.OPTIONS_AFFICHAGE:
      return "displayOptions";
    case PrintTab.IMAGES_SECONDAIRES:
      return "secondaryImages";
    case PrintTab.PAGE_TITRE:
      return "coverPage";
    case PrintTab.ENREGISTREMENTS_LIES:
      return "relatedRecords";
    case PrintTab.MISE_EN_PAGE:
      return "pageLayout";
    case PrintTab.PREVISUALISATION:
      return "preview";
    default:
      return "";
  }
};

const MultiplePrintDrawerInner = ({
  multipleprintIds,
  menuItemTrigger,
  labeledTrigger,
}: MultiplePrintDrawerProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { keepPresent } = usePresentPrintFields();
  const {
    isLoading: presenceLoading,
    isPresent,
    showingEverything,
  } = useFieldPresenceContext();
  /** Dimensions image renseignées → règle « Dim. avec marges / Dim. image ». */
  const hasImageDimensions =
    !showingEverything && isPresent("showImageDimensions");

  const form = useForm<FormValues>({
    defaultValues: {
      template: "complete",
      settings: defaultSettingsFor("complete"),
      itemsPerPage: "1",
      coverPageTitle: "",
      coverPageParagraph1: "",
      coverPageParagraph2: "",
      coverImagePosition: "after_title",
      coverImageSize: "large",
      coverImageFile: null,
      showPagination: true,
      showPrintDate: true,
      binderImageSize: "large",
      textAlignment: "center",
      headerAlignment: "center",
      imageAlignment: "center",
      listTitle: "",
      selectedAttachmentIds: [],
      showThumbnails: true,
      summaryFields: {
        registrationNumber: false,
        artist: false,
        title: false,
        creationDate: false,
        dimensions: false,
        techniqueType: false,
      },
    },
  });
  const template = form.watch("template");
  const isVignettes = template === "vignettes";
  // Pour la logique propre aux fiches (groupes, réglages), « vignettes » n'a pas
  // de sens : on retombe sur un modèle fiche (non utilisé en vignettes).
  const ficheTemplate: MultiplePrintTemplate = isVignettes
    ? "complete"
    : template;
  const showExemplaires = form.watch("settings.showExemplaires");
  const formId = useId();

  const { mutateAsync: generatePdf, isPending } =
    useGenerateMultipleprintsPdf();
  const { mutateAsync: generateListText } = useGenerateListTextPrint();

  // Liste des multiples sélectionnés (vignette + titre), comme sur les œuvres.
  const { data: selectedMultiples = [] } = useGetAllMultiplePrints({
    where: { id: { in: multipleprintIds } },
    order: undefined,
    take: Math.max(multipleprintIds.length, 1),
    skip: 0,
  });

  // Images secondaires de tous les multiples sélectionnés (hors image de face).
  const { data: allAttachmentsRaw = [] } = useGetAllAttachments({
    where: {
      docId: { in: multipleprintIds },
      position: { notIn: ["estampesMultiplesDescriptionPhotoFaceOeuvre"] },
    },
    order: { order: "asc" },
  });
  const allAttachments = allAttachmentsRaw.filter(
    (a) => a.details !== '{"defaultImage":true}',
  );
  const selectedAttachmentIds = form.watch("selectedAttachmentIds");

  useEffect(() => {
    if (template === "vignettes") return; // pas de réglages de fiche en vignettes
    form.setValue("settings", defaultSettingsFor(ficheTemplate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Cover page (namespacée par modèle, partagée avec la fiche unique).
  const coverPrintType = `multipleprint-${template}`;
  const { instanceData } = useAuth();
  const { data: savedCovers = [] } = useGetCoverPages(coverPrintType);
  const { mutateAsync: createCoverPage, isPending: isSavingCover } =
    useCreateCoverPage();
  const { mutateAsync: updateCoverPage, isPending: isUpdatingCover } =
    useUpdateCoverPage();
  const { mutateAsync: deleteCoverPage } = useDeleteCoverPage();

  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(null);
  const [selectedCoverImageLink, setSelectedCoverImageLink] = useState<
    string | null
  >(null);
  const [existingCoverImageUrl, setExistingCoverImageUrl] = useState<
    string | null
  >(null);
  const [existingCoverImageLink, setExistingCoverImageLink] = useState<
    string | null
  >(null);
  const [saveModelName, setSaveModelName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inlinePreviewURL, setInlinePreviewURL] = useState<string | null>(null);
  const [downloadFileName, setDownloadFileName] = useState<string | undefined>();
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isWordPending, setIsWordPending] = useState(false);

  const handleLoadCover = (cover: CoverPage) => {
    setSelectedCoverId(cover.id);
    setSelectedCoverImageLink(cover.imageLink ?? null);
    setExistingCoverImageUrl(cover.imageUrl ?? null);
    setExistingCoverImageLink(cover.imageLink ?? null);
    form.setValue("coverPageTitle", cover.title ?? "");
    form.setValue("coverPageParagraph1", cover.paragraph1 ?? "");
    form.setValue("coverPageParagraph2", cover.paragraph2 ?? "");
    form.setValue(
      "coverImagePosition",
      (cover.imagePosition as FormValues["coverImagePosition"]) ??
        "after_title",
    );
    form.setValue(
      "coverImageSize",
      (cover.imageSize as FormValues["coverImageSize"]) ?? "large",
    );
    form.setValue("coverImageFile", null);
  };

  const handleDeselectCover = () => {
    setSelectedCoverId(null);
    setSelectedCoverImageLink(null);
    setExistingCoverImageUrl(null);
    setExistingCoverImageLink(null);
    form.setValue("coverPageTitle", "");
    form.setValue("coverPageParagraph1", "");
    form.setValue("coverPageParagraph2", "");
    form.setValue("coverImagePosition", "header");
    form.setValue("coverImageSize", "large");
    form.setValue("coverImageFile", null);
  };

  const handleUpdateCover = async () => {
    if (!selectedCoverId) return;
    const values = form.getValues();
    let imageLink: string | null = selectedCoverImageLink;
    const oldLink = existingCoverImageLink ?? undefined;
    if (values.coverImageFile && instanceData) {
      try {
        const attachment = await uploadCoverPageImage(
          values.coverImageFile,
          instanceData.s3Bucket,
          instanceData.id,
          oldLink,
        );
        if (attachment?.link) {
          imageLink = attachment.link;
          setSelectedCoverImageLink(attachment.link);
          setExistingCoverImageLink(attachment.link);
        }
      } catch (_) {
        // upload échoué — on garde l'image existante
      }
    }
    await updateCoverPage({
      id: selectedCoverId,
      dto: {
        title: normalizeRichText(values.coverPageTitle),
        paragraph1: normalizeRichText(values.coverPageParagraph1),
        paragraph2: normalizeRichText(values.coverPageParagraph2),
        imageLink,
        imagePosition: values.coverImagePosition,
        imageSize: values.coverImageSize,
      },
    });
    toast({
      title: vt("coverPageUpdatedTitle", "Page de titre mise à jour"),
      variant: "success",
    });
  };

  const handleSaveCover = async () => {
    if (!saveModelName.trim()) return;
    const values = form.getValues();
    let imageLink: string | undefined;
    if (values.coverImageFile && instanceData) {
      const attachment = await uploadCoverPageImage(
        values.coverImageFile,
        instanceData.s3Bucket,
        instanceData.id,
      );
      imageLink = attachment.link;
      setSelectedCoverImageLink(attachment.link);
    } else if (selectedCoverImageLink) {
      imageLink = selectedCoverImageLink;
    }
    const newCover = await createCoverPage({
      name: saveModelName.trim(),
      printType: coverPrintType,
      title: normalizeRichText(values.coverPageTitle),
      paragraph1: normalizeRichText(values.coverPageParagraph1),
      paragraph2: normalizeRichText(values.coverPageParagraph2),
      imageLink,
      imagePosition: values.coverImagePosition,
      imageSize: values.coverImageSize,
    });
    setSelectedCoverId(newCover.id);
    setSaveModelName("");
    setShowSaveInput(false);
  };

  const watchedCoverTitle = form.watch("coverPageTitle");
  const watchedCoverParagraph1 = form.watch("coverPageParagraph1");
  const watchedCoverParagraph2 = form.watch("coverPageParagraph2");
  const watchedCoverImageFile = form.watch("coverImageFile");
  const hasUnsavedCover =
    !selectedCoverId &&
    (!isEmptyRichText(watchedCoverTitle) ||
      !isEmptyRichText(watchedCoverParagraph1) ||
      !isEmptyRichText(watchedCoverParagraph2) ||
      !!watchedCoverImageFile ||
      !!selectedCoverImageLink);

  // « Enreg. liés » seulement si le modèle en a (Complète). Les « Autres images »
  // sont proposées (parité œuvre) : la sélection s'applique à chaque multiple.
  const hasRelatedRecords = getGroupFields(ficheTemplate, "relations").length > 0;
  const availableTabs = getTabsForModel(templateToModel(template)).filter(
    (tab) => tab.id !== PrintTab.ENREGISTREMENTS_LIES || hasRelatedRecords,
  );
  const availableTabValues = availableTabs.map((tab) => printTabToValue(tab.id));

  const [activeTab, setActiveTab] = useState<string>(
    availableTabValues[0] ?? "displayOptions",
  );
  useEffect(() => {
    if (!availableTabValues.includes(activeTab)) {
      setActiveTab(availableTabValues[0] ?? "displayOptions");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const currentTabIndex = availableTabValues.indexOf(activeTab);
  const isLastTab = currentTabIndex === availableTabValues.length - 1;
  const isOnPreviewTab = activeTab === "preview";

  const buildParams = async (format: "pdf" | "word") => {
    const data = form.getValues();
    const settings: MultiplePrintSettings = {
      ...data.settings,
      showSecondaryImages: data.selectedAttachmentIds.length > 0,
    };
    for (const key of Object.keys(settings)) {
      if (key !== "exemplaireFields" && !keepPresent(key)) {
        settings[key as keyof MultiplePrintSettings] = false as never;
      }
    }
    settings.showCopyNumber = false;
    if (hasImageDimensions) {
      settings.hideDimensionLabel = false;
    }
    let coverImageLink = selectedCoverImageLink ?? undefined;
    if (!selectedCoverId && data.coverImageFile && !coverImageLink && instanceData) {
      try {
        const attachment = await uploadCoverPageImage(
          data.coverImageFile,
          instanceData.s3Bucket,
          instanceData.id,
        );
        coverImageLink = attachment.link;
        setSelectedCoverImageLink(attachment.link);
      } catch (_) {
        // upload échoué — on génère sans l'image
      }
    }
    const hasInlineCover =
      !selectedCoverId &&
      (!isEmptyRichText(data.coverPageTitle) ||
        !isEmptyRichText(data.coverPageParagraph1) ||
        !isEmptyRichText(data.coverPageParagraph2) ||
        !!coverImageLink);
    return {
      lang: i18n.language as SupportedLocale,
      template: data.template,
      multipleprintIds,
      format,
      settings,
      selectedAttachmentIds: data.selectedAttachmentIds,
      itemsPerPage: data.itemsPerPage,
      coverPageId: selectedCoverId ?? undefined,
      coverPage: hasInlineCover
        ? {
            title: normalizeRichText(data.coverPageTitle),
            paragraph1: normalizeRichText(data.coverPageParagraph1),
            paragraph2: normalizeRichText(data.coverPageParagraph2),
            imageLink: coverImageLink ?? null,
            imageSize: data.coverImageSize ?? "large",
            imagePosition: data.coverImagePosition ?? "after_title",
          }
        : undefined,
      showPagination: data.showPagination,
      showPrintDate: data.showPrintDate,
      listTitle: data.listTitle,
      binderImageSize: data.binderImageSize,
      textAlignment: data.template === "binder" ? data.textAlignment : "left",
      headerAlignment:
        data.template === "binder" ? data.headerAlignment : undefined,
      imageAlignment:
        data.template === "binder" ? data.imageAlignment : undefined,
    };
  };

  /** Paramètres du modèle Vignettes (liste-texte, endpoint générique). */
  const buildVignetteParams = (format: "pdf" | "word") => {
    const data = form.getValues();
    return {
      entityType: "multipleprint" as const,
      ids: multipleprintIds,
      lang: i18n.language as SupportedLocale,
      format,
      settings: {
        showThumbnails: data.showThumbnails ?? true,
        summaryFields: data.summaryFields,
        showPagination: data.showPagination,
        showPrintDate: data.showPrintDate,
        listTitle: data.listTitle,
        textAlignment: "left" as const,
      },
    };
  };

  /** Dispatch : vignettes → liste-texte ; sinon fiches dashboard. */
  const runGenerate = async (format: "pdf" | "word") =>
    isVignettes
      ? generateListText(buildVignetteParams(format))
      : generatePdf(await buildParams(format));

  const generatePreview = async (forceRetry = false) => {
    if (isGeneratingPreview || (inlinePreviewURL && !forceRetry)) return;
    setIsGeneratingPreview(true);
    setPreviewError(null);
    if (forceRetry && inlinePreviewURL) {
      URL.revokeObjectURL(inlinePreviewURL);
      setInlinePreviewURL(null);
    }
    try {
      const { file, filename } = await runGenerate("pdf");
      setDownloadFileName(filename);
      setInlinePreviewURL(URL.createObjectURL(file));
    } catch (error) {
      console.error("Failed to generate preview:", error);
      setPreviewError(
        vt("previewGenerationError", "Erreur lors de la génération de l'aperçu"),
      );
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const resetPreview = () => {
    if (inlinePreviewURL) {
      URL.revokeObjectURL(inlinePreviewURL);
      setInlinePreviewURL(null);
    }
    setPreviewError(null);
  };

  const handleNextTab = () => {
    if (currentTabIndex < availableTabValues.length - 1) {
      const nextTab = availableTabValues[currentTabIndex + 1];
      setActiveTab(nextTab);
      if (nextTab === "preview") generatePreview();
    }
  };

  const handleTabChange = (value: string) => {
    const targetIndex = availableTabValues.indexOf(value);
    if (targetIndex <= currentTabIndex) {
      if (activeTab === "preview" && value !== "preview") resetPreview();
      setActiveTab(value);
    }
  };

  const onSubmit = () => {
    if (!isOnPreviewTab) handleNextTab();
  };

  const handleDownloadPdf = () => {
    if (!inlinePreviewURL) return;
    const link = document.createElement("a");
    link.href = inlinePreviewURL;
    link.download = downloadFileName ?? "Multiples.pdf";
    link.click();
  };

  const handleDownloadWord = async () => {
    const progress = toast({
      title: vt("wordExportInProgressTitle", "Génération du document Word…"),
      description: vt(
        "wordExportInProgressDescription",
        "Merci de patienter, le téléchargement démarrera automatiquement.",
      ),
    });
    setIsWordPending(true);
    try {
      const { file, filename } = await runGenerate("word");
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? "Multiples.docx";
      link.click();
      URL.revokeObjectURL(url);
      progress.dismiss();
      toast({
        title: vt("wordExportDoneTitle", "Document Word prêt"),
        variant: "success",
      });
    } catch (error) {
      progress.dismiss();
      toast({
        title: vt(
          "wordExportErrorTitle",
          "Échec de la génération du document Word",
        ),
        description: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
    } finally {
      setIsWordPending(false);
    }
  };

  const resetDrawerState = useCallback(() => {
    resetPreview();
    setDownloadFileName(undefined);
    setIsGeneratingPreview(false);
    setActiveTab(availableTabValues[0] ?? "displayOptions");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlinePreviewURL]);

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) resetDrawerState();
  };

  const tabLabel = (value: string): string => {
    switch (value) {
      case "displayOptions":
        return vt("printDisplayOptions", "Options d'affichage");
      case "secondaryImages":
        return vt("bo.print.tabSecondaryImages", "Autres images");
      case "coverPage":
        return vt("printCoverPage", "Page titre");
      case "relatedRecords":
        return vt("printRelatedRecords", "Enregistrements liés");
      case "pageLayout":
        return vt("printPageLayout", "Mise en page");
      case "preview":
        return vt("printPreview", "Aperçu");
      default:
        return value;
    }
  };

  const filterDisplayField = (field: string): boolean => {
    if ((EXEMPLAIRE_ONLY_SETTING_KEYS as readonly string[]).includes(field)) {
      return false;
    }
    if (field === "hideDimensionLabel" && hasImageDimensions) {
      return false;
    }
    return keepPresent(field);
  };

  /** Champs réellement proposés (présents) du modèle courant. */
  const presentDisplayFields = Object.values(getSettingsGroups())
    .flatMap((g) => getGroupFields(ficheTemplate, g.key))
    .filter(filterDisplayField);

  /**
   * Coche/décoche tous les champs présents du modèle ET les champs des
   * exemplaires (retour client : « Tout sélectionner » doit aussi prendre les
   * champs des exemplaires).
   */
  const setAllDisplayFields = (val: boolean) => {
    const next = { ...form.getValues("settings") };
    for (const f of presentDisplayFields) {
      next[f as keyof MultiplePrintSettings] = val as never;
    }
    const exFields = { ...(next.exemplaireFields ?? {}) };
    for (const k of exemplaireFieldKeys) exFields[k] = val;
    next.exemplaireFields = exFields;
    form.setValue("settings", next);
  };

  // État de la case maître « Tout sélectionner » : coché / indéterminé / vide.
  // Inclut les champs des exemplaires quand la liste des exemplaires est active.
  const watchedSettings = form.watch("settings");
  const exKeysActive = showExemplaires ? exemplaireFieldKeys : [];
  const exFieldsVal = watchedSettings?.exemplaireFields ?? {};
  const allFieldsSelected =
    presentDisplayFields.length > 0 &&
    presentDisplayFields.every(
      (f) => watchedSettings?.[f as keyof MultiplePrintSettings],
    ) &&
    exKeysActive.every((k) => exFieldsVal[k]);
  const someFieldsSelected =
    presentDisplayFields.some(
      (f) => watchedSettings?.[f as keyof MultiplePrintSettings],
    ) || exKeysActive.some((k) => exFieldsVal[k]);
  const masterFieldsState: boolean | "indeterminate" = allFieldsSelected
    ? true
    : someFieldsSelected
      ? "indeterminate"
      : false;

  const renderSettingsCheckbox = (field: string) => (
    <FormField
      key={field}
      control={form.control}
      name={`settings.${field}` as never}
      render={({ field: formField }) => (
        <FormItem className="flex flex-row gap-2 space-y-0">
          <FormControl>
            <Checkbox
              checked={formField.value as boolean}
              onCheckedChange={formField.onChange}
            />
          </FormControl>
          <FormLabel className="text-xs font-normal">
            {field === "showLongDescription"
              ? vt("epreuves", "Épreuves - Tirage")
              : field === "showDimensions" && hasImageDimensions
                ? vt(
                    "singleMultipleprintExemplaireDimensionsSheet",
                    "Dim. avec marges",
                  )
                : field === "showImageDimensions"
                  ? vt(
                      "singleMultipleprintExemplaireDimensionsImage",
                      "Dim. image",
                    )
                  : vt(
                      `bo.print.mp.${field}`,
                      MULTIPLEPRINT_SETTING_LABELS[field] ?? field,
                    )}
          </FormLabel>
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ isLoading: isPending || isGeneratingPreview }}
          hideText
          iconSize={18}
          menuItemTrigger={menuItemTrigger}
          labeledTrigger={labeledTrigger}
          isLastTab={isLastTab}
          isOnPreviewTab={isOnPreviewTab}
          onNextTab={handleNextTab}
          open={drawerOpen}
          keepContentMounted
          onOpenChange={handleDrawerOpenChange}
          previewFooter={
            <div className="flex flex-row flex-1 items-center gap-4 p-4">
              <Button
                type="button"
                variant="outline"
                className="flex flex-1 bg-white"
                onClick={() => handleDrawerOpenChange(false)}
              >
                {t("actionCancel")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={
                      !inlinePreviewURL || isGeneratingPreview || isWordPending
                    }
                    className="flex flex-1 items-center justify-center gap-2"
                  >
                    {isWordPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {vt("printDownload", "Télécharger")}
                        <ChevronDown className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDownloadPdf}>
                    {vt("downloadPdf", "PDF")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDownloadWord}
                    disabled={isWordPending}
                  >
                    {isWordPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {vt("downloadWord", "Word (.docx)")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        >
          <div className="flex flex-col gap-4 max-h-[calc(100vh-180px)] overflow-y-auto pr-2">
            {/* Liste des multiples à exporter (vignette + titre), parité œuvre */}
            <div className="flex flex-col gap-4 items-center">
              <h3 className="self-start text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                {vt(
                  "bo.print.mp.listToExport",
                  "Liste des multiples à exporter",
                )}
              </h3>
              <div className="grid grid-cols-3 gap-3 w-full max-h-[120px] overflow-y-auto pr-2">
                {selectedMultiples.map((mp) => (
                  <SelectedRecord
                    key={mp.id}
                    image={getDefaultImage(
                      mp.attachments ?? [],
                      "multipleprint",
                      "publicUrl",
                    )}
                    title={
                      [
                        mp.estampesMultiplesDescriptionTitre,
                        mp.estampesMultiplesDescriptionNumeroEnregistrement,
                      ]
                        .filter(Boolean)
                        .join(" - ") || t("untitled")
                    }
                  />
                ))}
              </div>
            </div>

            <Separator />

            {/* Modèle d'export */}
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                    {vt("printSelectModel", "Modèle d'export")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="complete">
                        {vt(
                          "bo.print.mp.modelComplete",
                          "Fiche multiple – Petite photo",
                        )}
                      </SelectItem>
                      <SelectItem value="binder">
                        {vt(
                          "bo.print.mp.modelBinder",
                          "Fiche multiple – Grande photo",
                        )}
                      </SelectItem>
                      <SelectItem value="vignettes">
                        {vt(
                          "bo.print.mp.modelVignettes",
                          "Liste de multiples – Vignettes",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <Separator />

            <Tabs
              className="w-full flex flex-col items-center"
              value={activeTab}
              onValueChange={handleTabChange}
            >
              <TabsList className="h-9 flex-wrap">
                {availableTabs.map((tab, index) => {
                  const tabValue = printTabToValue(tab.id);
                  const isActive = activeTab === tabValue;
                  const isDisabled = index > currentTabIndex;
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tabValue}
                      disabled={isDisabled}
                      className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 ${
                        !isActive ? "bg-slate-100" : ""
                      } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <Badge
                        variant={isActive ? "outlinedActive" : "outlined"}
                        className="h-4 min-w-4 text-[10px] px-1 flex items-center justify-center leading-none"
                      >
                        {index + 1}
                      </Badge>
                      {tabLabel(tabValue)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* ===================== OPTIONS D'AFFICHAGE ===================== */}
              <TabsContent value="displayOptions" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("printDisplayOptionsTitle", "Options d'affichage")}
                </h3>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <Checkbox
                        checked={masterFieldsState}
                        onCheckedChange={() =>
                          setAllDisplayFields(masterFieldsState !== true)
                        }
                      />
                      <span className="text-sm font-normal text-[#008BD4]">
                        {vt("selectAll", "Tout sélectionner")}
                      </span>
                    </label>
                    <button
                      type="button"
                      className="text-sm font-normal text-muted-foreground cursor-pointer"
                      onClick={() => setAllDisplayFields(false)}
                    >
                      {vt("deselectAll", "Tout désélectionner")}
                    </button>
                  </div>
                  <ShowAllFieldsToggle />
                </div>
                <div className="relative">
                  <PresenceLoadingHint />
                  <div
                    className={`grid grid-cols-2 gap-x-8 gap-y-0 ${presenceLoadingClassName(
                      presenceLoading,
                    )}`}
                    aria-busy={presenceLoading}
                  >
                    {(["left", "right"] as const).map((col) => (
                      <div key={col}>
                        {Object.values(getSettingsGroups())
                          .filter(
                            (group) =>
                              group.column === col && group.key !== "relations",
                          )
                          .map((group) => {
                            const fields = getGroupFields(
                              ficheTemplate,
                              group.key,
                            ).filter(filterDisplayField);
                            if (fields.length === 0) return null;
                            return (
                              <div key={group.key} className="mb-6">
                                <h3 className="text-sm font-semibold mb-3">
                                  {vt(group.titleKey, group.key)}
                                </h3>
                                <div className="space-y-2">
                                  {fields.map((field) =>
                                    renderSettingsCheckbox(field),
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ))}
                  </div>
                </div>

                {showExemplaires && (
                  <>
                    <Separator className="my-4" />
                    <div className="space-y-2">
                      <FormLabel className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                        {vt(
                          "bo.print.mp.exemplaireFields",
                          "Champs des exemplaires",
                        )}
                      </FormLabel>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                        {exemplaireFieldKeys.map((key) => (
                          <FormField
                            key={key}
                            control={form.control}
                            name={`settings.exemplaireFields.${key}` as never}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center gap-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value as boolean}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <FormLabel className="text-xs font-normal cursor-pointer">
                                  {vt(
                                    `bo.print.mp.ex.${key}`,
                                    EXEMPLAIRE_FIELD_LABELS[key],
                                  )}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* ===================== ENREGISTREMENTS LIÉS ===================== */}
              <TabsContent value="relatedRecords" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("printRelatedRecords", "Enregistrements liés")}
                </h3>
                <div className="space-y-2 w-fit">
                  {getGroupFields(ficheTemplate, "relations").map((field) =>
                    renderSettingsCheckbox(field),
                  )}
                </div>
              </TabsContent>

              {/* ===================== AUTRES IMAGES ===================== */}
              <TabsContent value="secondaryImages" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("bo.print.tabSecondaryImages", "Autres images")}
                </h3>
                {allAttachments.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    {vt("noSecondaryImages", "Aucune image secondaire")}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-start gap-2 mb-4">
                      <Checkbox
                        checked={
                          selectedAttachmentIds.length === allAttachments.length
                        }
                        onCheckedChange={(checked) => {
                          form.setValue(
                            "selectedAttachmentIds",
                            checked ? allAttachments.map((a) => a.id) : [],
                          );
                        }}
                      />
                      <label
                        className="text-sm font-normal cursor-pointer"
                        style={{ color: "#008BD4" }}
                      >
                        {vt("selectAll", "Tout cocher")}
                      </label>
                    </div>
                    <div className="grid grid-cols-6 gap-3">
                      {allAttachments.map((attachment) => {
                        const isSelected = selectedAttachmentIds.includes(
                          attachment.id,
                        );
                        return (
                          <div key={attachment.id} className="flex flex-col gap-1">
                            <div
                              className="relative aspect-square rounded-md overflow-hidden border-2 transition-all cursor-pointer hover:border-primary"
                              style={{
                                borderColor: isSelected
                                  ? "hsl(var(--primary))"
                                  : "hsl(var(--border))",
                              }}
                              onClick={() => {
                                const ids = form.getValues(
                                  "selectedAttachmentIds",
                                );
                                form.setValue(
                                  "selectedAttachmentIds",
                                  isSelected
                                    ? ids.filter((id) => id !== attachment.id)
                                    : [...ids, attachment.id],
                                );
                              }}
                            >
                              <img
                                src={
                                  attachment.mediumPublicUrl ||
                                  attachment.smallPublicUrl ||
                                  ""
                                }
                                alt={
                                  attachment.textAlt?.[
                                    i18n.language as SupportedLocale
                                  ] || ""
                                }
                                className="w-full h-full object-cover"
                              />
                              <div
                                className="absolute top-2 right-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    const ids = form.getValues(
                                      "selectedAttachmentIds",
                                    );
                                    const already = ids.includes(attachment.id);
                                    if (checked && !already) {
                                      form.setValue("selectedAttachmentIds", [
                                        ...ids,
                                        attachment.id,
                                      ]);
                                    } else if (checked === false && already) {
                                      form.setValue(
                                        "selectedAttachmentIds",
                                        ids.filter((id) => id !== attachment.id),
                                      );
                                    }
                                  }}
                                  className="bg-white"
                                />
                              </div>
                            </div>
                            {attachment.position && (
                              <span className="text-xs text-center font-medium truncate">
                                {t(attachment.position)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* ===================== PAGE TITRE ===================== */}
              <TabsContent value="coverPage" className="mt-4 w-full">
                <div className="space-y-6 px-8">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                    {vt("printCoverPageTitle", "Page titre")}
                  </h3>

                  {savedCovers.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BookMarked className="size-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">
                          {vt("coverPageSavedModels", "Modèles enregistrés")}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {savedCovers.map((cover) => {
                          const isSelected = selectedCoverId === cover.id;
                          return (
                            <div
                              key={cover.id}
                              onClick={() =>
                                isSelected
                                  ? handleDeselectCover()
                                  : handleLoadCover(cover)
                              }
                              className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border text-xs font-medium cursor-pointer transition-all select-none ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50 text-blue-700"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {isSelected && (
                                <Check className="size-3 text-blue-600 shrink-0" />
                              )}
                              <span>{cover.name}</span>
                              <button
                                type="button"
                                className={`ml-0.5 rounded-full p-0.5 transition-colors ${
                                  isSelected
                                    ? "text-blue-400 hover:text-blue-700 hover:bg-blue-100"
                                    : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) handleDeselectCover();
                                  deleteCoverPage(cover.id);
                                }}
                              >
                                <X className="size-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <Separator className="mt-4" />
                    </div>
                  )}

                  {(
                    [
                      ["coverPageTitle", "Titre"],
                      ["coverPageParagraph1", "Paragraphe 1"],
                      ["coverPageParagraph2", "Paragraphe 2"],
                    ] as const
                  ).map(([name, label]) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold">
                            {vt(name, label)}
                          </FormLabel>
                          <FormControl>
                            <Tiptap
                              {...field}
                              label=""
                              description={field.value || ""}
                              onChange={field.onChange}
                              defaultTextAlign="center"
                              toolbarPreset="complete"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  ))}

                  <Separator className="my-6" />

                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-4">
                      {vt("coverImageTitle", "Image de couverture")}
                    </h4>
                    <div className="flex gap-6">
                      <div className="flex-1">
                        <FormField
                          control={form.control}
                          name="coverImagePosition"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">
                                {vt("coverImagePosition", "Position de l'image")}
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="header">
                                    {vt(
                                      "bo.print.coverImagePositionHeader",
                                      "Avant le titre",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="after_title">
                                    {vt(
                                      "bo.print.coverImagePositionAfterTitle",
                                      "Après le titre",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="after_para1">
                                    {vt(
                                      "bo.print.coverImagePositionAfterPara1",
                                      "Après le paragraphe 1",
                                    )}
                                  </SelectItem>
                                  <SelectItem value="footer">
                                    {vt(
                                      "bo.print.coverImagePositionFooter",
                                      "Après le paragraphe 2",
                                    )}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <FormField
                          control={form.control}
                          name="coverImageSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">
                                {vt("coverImageSize", "Taille de l'image")}
                              </FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  className="flex flex-row gap-4 mt-4"
                                >
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="large" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {vt("coverImageSizeLarge", "Grande")}
                                    </FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="small" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {vt("coverImageSizeSmall", "Petite")}
                                    </FormLabel>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <FormField
                        control={form.control}
                        name="coverImageFile"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              {field.value ? (
                                <FileItem
                                  file={field.value}
                                  image={
                                    <img
                                      src={URL.createObjectURL(field.value)}
                                      alt="preview"
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  }
                                  onDelete={() => {
                                    field.onChange(null);
                                    setSelectedCoverImageLink(null);
                                    setExistingCoverImageUrl(null);
                                  }}
                                />
                              ) : existingCoverImageUrl ? (
                                <div className="flex items-center gap-3 p-3 border rounded-md">
                                  <img
                                    src={existingCoverImageUrl}
                                    alt="cover"
                                    className="w-10 h-10 object-cover rounded"
                                  />
                                  <span className="text-sm text-muted-foreground flex-1">
                                    {vt("coverImageFromModel", "Image du modèle")}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCoverImageLink(null);
                                      setExistingCoverImageUrl(null);
                                    }}
                                  >
                                    ×
                                  </Button>
                                </div>
                              ) : (
                                <FileDropzone
                                  onDrop={(files) => {
                                    if (files.length > 0) {
                                      field.onChange(files[0]);
                                      setExistingCoverImageUrl(null);
                                      setSelectedCoverImageLink(null);
                                    }
                                  }}
                                  multiple={false}
                                  className="border-solid"
                                >
                                  {() => (
                                    <div className="flex flex-col items-center justify-center gap-6">
                                      <Thumbnail>
                                        <CloudUpload className="text-gray-700 size-6" />
                                      </Thumbnail>
                                      <LoadImageDropzoneCta dragCtaKey="loadImage.dragCtaSingle" />
                                    </div>
                                  )}
                                </FileDropzone>
                              )}
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {hasUnsavedCover && !showSaveInput && (
                    <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        {vt(
                          "coverPageUnsavedHint",
                          "Cette page de couverture n'est pas enregistrée comme modèle. Enregistrez-la pour la réutiliser (elle s'imprimera même sans être enregistrée).",
                        )}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-end">
                    {selectedCoverId ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleUpdateCover}
                        disabled={isUpdatingCover}
                      >
                        <Save className="size-3.5 mr-1.5" />
                        {vt("saveCoverPageChanges", "Enregistrer les modifications")}
                      </Button>
                    ) : showSaveInput ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={saveModelName}
                          onChange={(e) => setSaveModelName(e.target.value)}
                          placeholder={vt("coverPageModelName", "Nom du modèle")}
                          className="flex-1 text-sm"
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleSaveCover()
                          }
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveCover}
                          disabled={!saveModelName.trim() || isSavingCover}
                        >
                          <Save className="size-3.5 mr-1.5" />
                          {t("save")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowSaveInput(false);
                            setSaveModelName("");
                          }}
                        >
                          {t("cancel")}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSaveInput(true)}
                      >
                        <Save className="size-3.5 mr-1.5" />
                        {vt("coverPageSaveAsModel", "Enregistrer comme modèle")}
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ===================== MISE EN PAGE ===================== */}
              <TabsContent value="pageLayout" className="mt-4 w-full">
                <div className="space-y-6 px-8">
                  {isVignettes ? (
                    <>
                      <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                        {vt("printPageLayoutTitle", "Mise en page")}
                      </h3>
                      <FormField
                        control={form.control}
                        name="showThumbnails"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt("showThumbnails", "Afficher les vignettes")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                      <div>
                        <FormLabel className="text-sm font-medium">
                          {vt("listSummaryFields", "Champs du résumé")}
                        </FormLabel>
                        <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-2">
                          {(
                            [
                              ["registrationNumber", "N° d'enregistrement"],
                              ["artist", "Artiste"],
                              ["title", "Titre"],
                              ["creationDate", "Date de création"],
                              ["dimensions", "Dimensions"],
                              ["techniqueType", "Technique"],
                            ] as const
                          ).map(([key, label]) => (
                            <FormField
                              key={key}
                              control={form.control}
                              name={`summaryFields.${key}` as never}
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value as boolean}
                                      onCheckedChange={field.onChange}
                                    />
                                  </FormControl>
                                  <FormLabel className="text-xs font-normal cursor-pointer">
                                    {vt(`bo.print.mp.vignette.${key}`, label)}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </div>
                      <FormField
                        control={form.control}
                        name="listTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-normal">
                              {vt("printDocumentTitleLabel", "Titre du document")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ""}
                                placeholder={vt(
                                  "printDocumentTitlePlaceholder",
                                  "Ex. Sélection expo X",
                                )}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="showPagination"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt("showPagination", "Afficher la pagination")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="showPrintDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt(
                                "showPrintDate",
                                "Afficher la date d'impression",
                              )}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </>
                  ) : template === "binder" ? (
                    <>
                      <FormField
                        control={form.control}
                        name="binderImageSize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("binderImageSize", "Taille de l'image")}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-row gap-6 mt-2"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="large" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("binderImageSizeLarge", "Grande")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="small" />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">
                                    {vt("binderImageSizeSmall", "Petite")}
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Separator />
                      {(
                        [
                          ["textAlignment", "Alignement du texte"],
                          ["headerAlignment", "Alignement du header"],
                          ["imageAlignment", "Alignement de l'image"],
                        ] as const
                      ).map(([name, label]) => (
                        <FormField
                          key={name}
                          control={form.control}
                          name={name}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                {vt(name, label)}
                              </FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  className="flex flex-row gap-6 mt-2"
                                >
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="center" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {vt("alignCenter", "Centré")}
                                    </FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="left" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {vt("alignLeftFull", "Aligné à gauche")}
                                    </FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="right" />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {vt("alignRightFull", "Aligné à droite")}
                                    </FormLabel>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4]">
                        {vt("printPageLayoutTitle", "Mise en page")}
                      </h3>
                      <FormField
                        control={form.control}
                        name="itemsPerPage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              {vt("itemsPerPage", "Fiches par page")}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="1">
                                  {vt("onePerPage", "Une par page")}
                                </SelectItem>
                                <SelectItem value="3">
                                  {vt("threePerPage", "Trois par page")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="listTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-normal">
                              {vt("printDocumentTitleLabel", "Titre du document")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ""}
                                placeholder={vt(
                                  "printDocumentTitlePlaceholder",
                                  "Ex. Sélection expo X",
                                )}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="showPagination"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt("showPagination", "Afficher la pagination")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="showPrintDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {vt(
                                "showPrintDate",
                                "Afficher la date d'impression",
                              )}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
              </TabsContent>

              {/* ===================== APERÇU ===================== */}
              <TabsContent value="preview" className="mt-4 w-full">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#008BD4] mb-3">
                  {vt("printPreviewTitle", "Prévisualisation")}
                </h3>
                <div className="flex justify-center overflow-auto max-h-[calc(100vh-350px)] border rounded-lg bg-gray-50 p-4">
                  {isGeneratingPreview ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {vt("generatingPreview", "Génération de l'aperçu...")}
                      </span>
                    </div>
                  ) : previewError ? (
                    <div className="flex flex-col items-center justify-center gap-4 py-12">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                      <span className="text-sm text-destructive">
                        {previewError}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => generatePreview(true)}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        {vt("retry", "Réessayer")}
                      </Button>
                    </div>
                  ) : inlinePreviewURL ? (
                    <PDFViewer fileURL={inlinePreviewURL} />
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <span className="text-sm text-muted-foreground">
                        {vt(
                          "clickToGeneratePreview",
                          "Cliquez sur l'onglet pour générer l'aperçu",
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

const MultiplePrintDrawer = (props: MultiplePrintDrawerProps) => (
  <FieldPresenceProvider
    table="multipleprint"
    ids={props.multipleprintIds}
  >
    <MultiplePrintDrawerInner {...props} />
  </FieldPresenceProvider>
);

export default MultiplePrintDrawer;
